import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { PageHeader } from '@/components/core';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createLocationTableColumns } from '../../components/location-table-columns/location-table-columns';
import { LocationFormDialog } from '../../components/location-form-dialog/location-form-dialog';
import { useDeleteLocation, useGetLocations } from '../../hooks/use-locations';
import { DEFAULT_LOCATIONS_ORDER } from '../../graphql/queries';
import { Location, LocationsPagingResult } from '../../types/location.types';

export const LocationManagerPage = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.SETTINGS.LOCATIONS_CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.SETTINGS.LOCATIONS_UPDATE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.SETTINGS.LOCATIONS_DELETE);

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_LOCATIONS_ORDER,
  });
  const [formState, setFormState] = useState<{ open: boolean; location: Location | null }>({
    open: false,
    location: null,
  });
  const [deleteState, setDeleteState] = useState<{ open: boolean; location: Location | null }>({
    open: false,
    location: null,
  });

  const { data, isLoading, error } = useGetLocations({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { Locations?: LocationsPagingResult })?.Locations;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteLocation, isPending: isDeleting } = useDeleteLocation();

  const handleDeleteConfirm = async () => {
    if (!deleteState.location) return;
    await deleteLocation(deleteState.location.ItemId);
    setDeleteState({ open: false, location: null });
  };

  const columns = createLocationTableColumns({
    t,
    onEdit: (location) => setFormState({ open: true, location }),
    onDelete: (location) => setDeleteState({ open: true, location }),
    canUpdate,
    canDelete,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('LOCATIONS')}
        actions={
          canCreate
            ? [
                {
                  label: t('CREATE_LOCATION'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setFormState({ open: true, location: null }),
                },
              ]
            : []
        }
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <LocationFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, location: open ? prev.location : null }))
        }
        location={formState.location}
      />

      <ConfirmationModal
        open={deleteState.open}
        onOpenChange={(open) =>
          setDeleteState({ open, location: open ? deleteState.location : null })
        }
        title={t('DELETE_LOCATION')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
